import { MenuItem } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";

import { Patient } from "../../models/patient";
import { usePatientData } from "../../hooks/usePatientData";
import { useVisualisationData } from "../../hooks/useVisualisationData";
import { useWeeklyTargetsData } from "../../hooks/useWeeklyTargetsData";

interface PatientExportsMenuDownloadProps {
  patient: Patient;
  exportId: string;
  onClose: () => void;
}

export const PatientExportsMenuDownload: React.FC<PatientExportsMenuDownloadProps> = ({
  patient,
  exportId,
  onClose,
}) => {
  const { t } = useTranslation();

  // Get data for export functionality
  const patientData = usePatientData({
    patientId: patient.id,
    exportId: exportId,
  });

  const { series, dataset } = useVisualisationData({
    exportId: exportId,
    patientId: patient.id,
    timeframe: {}, // Get all data, not restricted by timeframe
  });

  const { goals, logs } = useWeeklyTargetsData({
    exportId: exportId,
    patientId: patient.id,
  });

  const formatTimestamp = (timestamp: number) => {
    try {
      return format(new Date(timestamp), "yyyy-MM-dd HH:mm");
    } catch (error) {
      console.error("Error formatting timestamp:", timestamp, error);
      return "Invalid date";
    }
  };

  // Helper function to set column widths
  const formatSheet = (worksheet: XLSX.WorkSheet, widths: number[]) => {
    // Set column widths
    const cols = widths.map((width) => ({ wch: width }));
    worksheet["!cols"] = cols as XLSX.ColInfo[];
    return worksheet;
  };

  const handleExport = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onClose();
    
    if (!patientData) return;

    try {
      const workbook = XLSX.utils.book_new();
      const exportDate = format(patientData.exportDate, "yyyy-MM-dd HH:mm");
      const sanitizedName = patient.name.replace(/[\\/:*?"<>|]/g, "_");
      const fileName = `${sanitizedName}_data_${exportDate}.xlsx`;

      // Add series data if available
      if (dataset && dataset.length > 0 && series && series.length > 0) {
        try {
          // Prepare data and sort chronologically (oldest to newest)
          const metricsData = dataset
            .flatMap((datapoint) => {
              // Skip datapoints with no values
              if (
                !datapoint.values ||
                Object.keys(datapoint.values).length === 0
              ) {
                return [];
              }

              return Object.entries(datapoint.values).map(([id, valueObj]) => {
                // Find corresponding series info
                const seriesInfo = series.find((s) => s.id === id);

                return {
                  metricName: seriesInfo?.name || id,
                  value: valueObj.value,
                  timestamp: datapoint.timestamp,
                } as const;
              });
            })
            .sort((a, b) => a.timestamp - b.timestamp); // Sort by timestamp ascending

          if (metricsData.length > 0) {
            const seriesData = [
              [
                String(t("common.metric") || "Metric"),
                String(t("common.value") || "Value"),
                String(t("common.timestamp") || "Timestamp"),
              ],
              ...metricsData.map((item) => {
                return [
                  item.metricName,
                  item.value.toString(),
                  formatTimestamp(item.timestamp),
                ] as [string, string, string];
              }),
            ];
            const seriesSheet = XLSX.utils.aoa_to_sheet(seriesData);
            // For chart data, we set wider columns for better readability
            formatSheet(seriesSheet, [40, 10, 25]);
            XLSX.utils.book_append_sheet(
              workbook,
              seriesSheet,
              String(t("common.series") || "Series"),
            );
          }
        } catch (error) {
          console.error("Error processing metrics data:", error);
        }
      }

      // Create experiences sheet
      if (patientData.data.experiences?.length) {
        const experiencesData = [
          [
            String(
              t("tabs.experiences.experiences-label") || "Positive Experiences",
            ),
            String(t("common.date") || "Date"),
          ],
          ...patientData.data.experiences.map((exp) => [
            exp.title,
            formatTimestamp(exp.timestamp),
          ]),
        ];
        const experiencesSheet = XLSX.utils.aoa_to_sheet(experiencesData);
        formatSheet(experiencesSheet, [40, 25]);
        XLSX.utils.book_append_sheet(
          workbook,
          experiencesSheet,
          String(t("tabs.experiences.tab-name") || "Positive Experiences"),
        );
      }

      // Create activities sheet
      if (patientData.data.positiveActivitiesLog?.length) {
        const activitiesData = [
          [
            String(
              t("tabs.activities.activities-label") || "Positive Activity",
            ),
            String(t("common.date") || "Date"),
          ],
          ...patientData.data.positiveActivitiesLog.map((activity) => {
            const activityName =
              patientData.data.positiveActivites?.find(
                (a) => a.id === activity.positiveActivityId,
              )?.name || String(t("common.no-data") || "No data");

            // Enhanced translation logic for activity names
            let translatedName = activityName;

            // Only attempt translation if we have a string
            if (typeof activityName === "string") {
              // First check if it's a recognizable activity pattern (e.g. "activity_01.activity_02")
              if (activityName.match(/activity_\d+\.activity_\d+/)) {
                // Try translating directly
                const directTranslation = t(activityName);
                // Only use the translation if it's different from the key (successful translation)
                if (directTranslation !== activityName) {
                  translatedName = directTranslation;
                }
              }
              // Check for possible nested translations in positive_activities namespace
              else if (activityName.match(/activity_\d+/)) {
                // Try various namespaces that might contain the translation
                const possibleKeys = [
                  `positive_activities.${activityName}`,
                  activityName,
                ];

                // Try each possible key
                for (const key of possibleKeys) {
                  const translation = t(key);
                  if (translation !== key) {
                    translatedName = translation;
                    break;
                  }
                }
              }
            }

            return [translatedName, formatTimestamp(activity.timestamp)];
          }),
        ];
        const activitiesSheet = XLSX.utils.aoa_to_sheet(activitiesData);
        formatSheet(activitiesSheet, [30, 20]);
        XLSX.utils.book_append_sheet(
          workbook,
          activitiesSheet,
          String(t("tabs.activities.tab-name") || "Positive Activities"),
        );
      }

      // Create goals sheet
      if (goals?.length) {
        const goalsData = [
          [
            String(t("common.title") || "Title"),
            String(t("common.active") || "Active"),
            String(t("common.repeating") || "Repeating"),
            String(t("common.count") || "Count"),
          ],
          ...goals.map((goal) => [
            goal.title,
            goal.active
              ? String(t("common.yes") || "Yes")
              : String(t("common.no") || "No"),
            goal.repeat
              ? String(t("common.yes") || "Yes")
              : String(t("common.no") || "No"),
            goal.count.toString(),
          ]),
        ];
        const goalsSheet = XLSX.utils.aoa_to_sheet(goalsData);
        formatSheet(goalsSheet, [40, 15, 15, 15]);
        XLSX.utils.book_append_sheet(
          workbook,
          goalsSheet,
          String(t("tabs.goals.tab-name") || "Weekly Goals"),
        );
      }

      // Create goal completions sheet
      if (logs?.length) {
        const completionsData = [
          [
            String(t("tabs.goals.tab-name") || "Weekly Goal"),
            String(t("common.completion-date") || "Completion Date"),
          ],
          ...logs.map((log) => [
            log.refTarget?.title || String(t("common.no-data") || "No data"),
            formatTimestamp(log.timestamp),
          ]),
        ];
        const completionsSheet = XLSX.utils.aoa_to_sheet(completionsData);
        formatSheet(completionsSheet, [40, 25]);
        XLSX.utils.book_append_sheet(
          workbook,
          completionsSheet,
          String(t("tabs.goals.completions-label") || "Achieved Goals"),
        );
      }

      // Create strengths sheet
      if (patientData.data.strengths?.length) {
        const strengthsData = [
          [String(t("tabs.more.strengths-label") || "Strength")],
          ...patientData.data.strengths.map((strength) => [strength.title]),
        ];
        const strengthsSheet = XLSX.utils.aoa_to_sheet(strengthsData);
        formatSheet(strengthsSheet, [60]);
        XLSX.utils.book_append_sheet(
          workbook,
          strengthsSheet,
          String(t("tabs.more.strengths-label") || "Strengths"),
        );
      }

      // Create notes sheet
      if (patientData.data.notes?.length) {
        const notesData = [
          [
            String(t("tabs.more.notes-label") || "Note"),
            String(t("common.date") || "Date"),
          ],
          ...patientData.data.notes.map((note) => [
            note.note,
            formatTimestamp(note.timestamp),
          ]),
        ];
        const notesSheet = XLSX.utils.aoa_to_sheet(notesData);
        formatSheet(notesSheet, [60, 25]);
        XLSX.utils.book_append_sheet(
          workbook,
          notesSheet,
          String(t("tabs.more.notes-label") || "Notes"),
        );
      }

      // Create crisis plan sheet
      if (patientData.data.crisisPlan?.length) {
        const crisis = patientData.data.crisisPlan[0];
        const crisisData = [
          [
            String(t("tabs.more.crisis-plan") || "Crisis Plan"),
            crisis.crisisPlan || String(t("common.no-data") || "No data"),
          ],
          [
            String(t("tabs.more.crisis-signs") || "Crisis Signs"),
            crisis.crisisAlerts || String(t("common.no-data") || "No data"),
          ],
          [
            String(t("tabs.more.crisis-measures") || "Long Term Measures"),
            crisis.longTermMeasures || String(t("common.no-data") || "No data"),
          ],
          [
            String(t("tabs.more.crisis-update") || "Last Update"),
            crisis.lastUpdateTimestamp
              ? formatTimestamp(crisis.lastUpdateTimestamp)
              : String(t("common.no-data") || "No data"),
          ],
        ];
        const crisisSheet = XLSX.utils.aoa_to_sheet(crisisData);
        formatSheet(crisisSheet, [20, 60]);
        XLSX.utils.book_append_sheet(
          workbook,
          crisisSheet,
          String(t("tabs.more.crisis-label") || "Crisis Plan"),
        );
      }

      // Set some helpful metadata
      workbook.Props = {
        Title: `Patient Data - ${patient.name}`,
        Subject: "Patient Health Data Export",
        Author: "Robin View App",
        CreatedDate: new Date(),
      };

      // Create and download the Excel file
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      alert(
        String(
          t("common.export-error") || "Error exporting data. Please try again.",
        ),
      );
    }
  };

  return (
    <MenuItem onClick={handleExport}>
      <DownloadIcon fontSize="small" sx={{ mr: 1.5 }} />
      {t("common.export-to-excel")}
    </MenuItem>
  );
};

export default PatientExportsMenuDownload;